import { type PropType, type Ref } from 'vue';
import { useEntitySearch } from './useEntitySearch';
import type {
  MemberSearchField,
  SearchContext,
  SearchFilter,
  SearchParams,
  Pagination,
  SortOrder,
} from '@/shared/lib';

type EntitySearchFunction<T> = (
  endpoint: string,
  searchParams: SearchParams
) => Promise<{ data: T[]; metadata: Pagination }>;

interface AdminListPageConfig<T extends { id: number }> {
  defaultSearchUrl: string;
  searchFunction: EntitySearchFunction<T>;
  defaultAmount?: number;
  searchFieldRef?: Ref<MemberSearchField>;
  skipShowAllPrefetch?: boolean;
}

interface AdminListPageProps {
  searchTerm?: string;
  sort?: string;
  order?: string;
  filter?: SearchFilter;
  context?: SearchContext;
  searchUrl?: string;
}

export function useAdminListPageSetup<T extends { id: number }>(
  config: AdminListPageConfig<T>,
  props: AdminListPageProps
) {
  const {
    items,
    allItems,
    hasMoreItems,
    loadingMore,
    isShowAllMode,
    isInfiniteScrollMode,
    loading,
    handleSearch,
    initSearch,
    metadata,
    currentPage,
    selectedQuantity,
    onPageChanged,
    handleQuantityChange,
    searchTerm,
    removeItems,
    cleanup,
    handleFilter,
    handleSortOptions,
    filter,
    isUsingFilters,
    sort,
    order,
  } = useEntitySearch<T>(
    {
      defaultSearchUrl: config.defaultSearchUrl,
      searchFunction: config.searchFunction,
      ...(config.searchFieldRef ? { searchFieldRef: config.searchFieldRef } : {}),
      ...(config.skipShowAllPrefetch ? { skipShowAllPrefetch: true } : {}),
    },
    {
      search: props.searchTerm ?? '',
      sort: { sort: props.sort, order: props.order as SortOrder },
      filter: props.filter ?? {},
      context: props.context ?? 'search',
      searchUrl: props.searchUrl && props.searchUrl !== '' ? props.searchUrl : config.defaultSearchUrl,
      page: 1,
      amount: config.defaultAmount ?? 10,
    }
  );

  const handleTableFilter = async (filters: Record<string, unknown>) => {
    await handleFilter(filters as SearchFilter);
  };

  return {
    items,
    allItems,
    hasMoreItems,
    loadingMore,
    isShowAllMode,
    isInfiniteScrollMode,
    loading,
    handleSearch,
    initSearch,
    metadata,
    currentPage,
    selectedQuantity,
    onPageChanged,
    handleQuantityChange,
    searchTerm,
    removeItems,
    cleanup,
    handleFilter,
    handleSortOptions,
    filter,
    isUsingFilters,
    handleTableFilter,
    sort,
    order,
  };
}

export function createAdminListPageProps() {
  return {
    searchTerm: { type: String, default: '' },
    sort: { type: String, default: '' },
    order: { type: String, default: '' },
    filter: {
      type: Object as PropType<SearchFilter>,
      default: () => ({}),
    },
    context: { type: String as PropType<SearchContext>, default: 'search' as SearchContext },
    searchUrl: { type: String, default: '' },
  };
}
